/**
 * Normalizes a URL by adding https:// protocol if missing and validating format
 * @param rawUrl - The raw URL string to normalize
 * @returns The normalized URL string or null if invalid
 */
export function normalizeUrl(rawUrl: string): string | null {
  let url = rawUrl.trim();

  // Reject malformed protocols explicitly
  if (url.startsWith('https//') || url.startsWith('http//')) {
    return null;
  }

  // Add https:// if no protocol
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    url = `https://${url}`;
  }

  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname;

    // Reject IPs and localhost
    const isIP = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname);
    const isLocalhost = hostname === 'localhost';
    if (!hostname.includes('.') || isIP || isLocalhost) {
      return null;
    }

    // Remove trailing slash (unless root)
    if (parsed.pathname !== '/' && parsed.pathname.endsWith('/')) {
      parsed.pathname = parsed.pathname.slice(0, -1);
    }

    return parsed.toString();
  } catch {
    return null;
  }
}

/**
 * Checks if a URL belongs to internal domains
 * @param uri - The URL to check
 * @param allowListedDomains - Optional array of allowed domains from API. Falls back to default Zillow domains if not provided.
 */
export function isInternalUrl(uri: string | null | undefined, allowListedDomains?: Array<string>): boolean {
  if (!uri) {
    return false;
  }

  try {
    const url = new URL(uri);
    const { hostname } = url;

    // Use provided domains or fall back to default internal domains
    const internalDomains = allowListedDomains || ['zillow.com', 'trulia.com', 'hotpads.com'];

    // Normalize domains to lowercase for case-insensitive comparison
    const normalizedDomains = internalDomains.map((domain) => domain.toLowerCase());

    // Check if hostname is exactly one of our domains or a valid subdomain
    return normalizedDomains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`));
  } catch (_) {
    return false;
  }
}
